import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star, ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";

const GoogleG = (props) => (
  <svg viewBox="0 0 48 48" {...props}>
    <path
      fill="#EA4335"
      d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
    />
    <path
      fill="#4285F4"
      d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
    />
    <path
      fill="#FBBC05"
      d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
    />
    <path
      fill="#34A853"
      d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
    />
  </svg>
);

const PER_PAGE = 3;
const DISPLAY_RATING = "4.9";

export default function Testimonials() {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(0);

  useEffect(() => {
    api.get("/testimonials").then((r) => setItems(r.data)).catch(() => setItems([]));
  }, []);

  const pages = useMemo(() => {
    const out = [];
    for (let i = 0; i < items.length; i += PER_PAGE) {
      out.push(items.slice(i, i + PER_PAGE));
    }
    return out;
  }, [items]);

  // Auto-rotation disabled to prevent layout jumps while users are reading.
  // Manual navigation via dots remains active.

  if (items.length === 0) return null;

  return (
    <section
      id="testimonios"
      className="section bg-[#fafafa]"
      data-testid="testimonials-section"
    >
      <div className="container-ie">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end mb-12">
          <div className="md:col-span-7">
            <div className="flex items-center gap-3 mb-5">
              <span className="dot-red" />
              <span className="overline">Reseñas reales</span>
            </div>
            <h2 className="font-display font-semibold tracking-tighter text-4xl md:text-5xl lg:text-6xl text-[#0a0a0a]">
              Lo que dicen nuestras
              <br /> <span className="italic font-light">alumnas y alumnos.</span>
            </h2>
          </div>
          <div className="md:col-span-5">
            <div
              data-testid="google-rating-summary"
              className="inline-flex items-center gap-3 bg-white border border-black/8 px-5 py-4"
            >
              <GoogleG className="w-7 h-7 shrink-0" />
              <div>
                <div className="flex items-center gap-1.5">
                  <span className="font-display font-bold text-lg leading-none text-[#0a0a0a]">
                    {DISPLAY_RATING}
                  </span>
                  <div className="flex items-center gap-[1px]">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <Star
                        key={i}
                        size={13}
                        className="fill-[#FBBC05] text-[#FBBC05]"
                        strokeWidth={0}
                      />
                    ))}
                  </div>
                </div>
                <div className="mt-1 text-[0.7rem] uppercase tracking-[0.16em] text-[#525252]">
                  Basado en 425+ reseñas
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="relative" data-testid="testimonials-slider">
          <AnimatePresence mode="wait">
            <motion.div
              key={page}
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5"
            >
              {pages[page]?.map((t) => (
                <article
                  key={t.id}
                  className="bg-white border border-black/8 p-5 hover:shadow-[0_18px_40px_-20px_rgba(0,0,0,0.18)] transition-shadow"
                  data-testid={`testimonial-card-${t.id}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <img
                        src={t.image}
                        alt={t.name}
                        loading="lazy"
                        className="w-10 h-10 rounded-full object-cover bg-neutral-200 shrink-0"
                      />
                      <div className="min-w-0">
                        <div className="font-semibold text-[0.95rem] text-[#0a0a0a] truncate">
                          {t.name}
                        </div>
                        <div className="text-[0.7rem] text-[#525252] truncate">
                          {t.date || "Reseña verificada"}
                        </div>
                      </div>
                    </div>
                    <GoogleG className="w-4 h-4 shrink-0 mt-1" />
                  </div>

                  <div className="mt-3 flex items-center gap-[1px]">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <Star
                        key={i}
                        size={14}
                        className={
                          i <= (t.rating || 5)
                            ? "fill-[#FBBC05] text-[#FBBC05]"
                            : "fill-transparent text-neutral-300"
                        }
                        strokeWidth={i <= (t.rating || 5) ? 0 : 1.5}
                      />
                    ))}
                  </div>

                  <p className="mt-3 text-sm text-[#0a0a0a]/80 leading-relaxed">
                    {t.quote}
                  </p>
                </article>
              ))}
            </motion.div>
          </AnimatePresence>

          {pages.length > 1 && (
            <div className="mt-10 flex items-center justify-between gap-6">
              <div className="flex items-center gap-1.5" data-testid="testimonials-dots">
                {pages.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setPage(i)}
                    data-testid={`testimonial-page-${i}`}
                    aria-label={`Ir a página ${i + 1}`}
                    className={`h-[3px] transition-all ${
                      i === page ? "w-10 bg-[#c40000]" : "w-5 bg-black/20"
                    }`}
                  />
                ))}
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  data-testid="testimonials-prev"
                  onClick={() =>
                    setPage((p) => (p - 1 + pages.length) % pages.length)
                  }
                  aria-label="Anterior"
                  className="w-11 h-11 border border-black/15 flex items-center justify-center bg-white hover:bg-[#0a0a0a] hover:text-white hover:border-[#0a0a0a] transition-colors"
                >
                  <ChevronLeft size={18} strokeWidth={1.5} />
                </button>
                <button
                  type="button"
                  data-testid="testimonials-next"
                  onClick={() => setPage((p) => (p + 1) % pages.length)}
                  aria-label="Siguiente"
                  className="w-11 h-11 border border-black/15 flex items-center justify-center bg-white hover:bg-[#0a0a0a] hover:text-white hover:border-[#0a0a0a] transition-colors"
                >
                  <ChevronRight size={18} strokeWidth={1.5} />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
